import logoVariants from '@/assets/img/logo';
import iconsPng from '@/assets/img/png/icons';
import BlueTopPage from '@/components/templates/BlueTopPage';
import LoadingPage from '@/components/templates/LoadingPage';
import authRoutes from '@/constants/routes/auth';
import useToast from '@/hooks/useToast';
import onboardingMessages from '@/messages/onboarding';
import NewPasswordForm from '@/modules/onboarding/user/NewPasswordForm';
import { Image, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import roles from '../../constants/roles.ts';
import { BACKOFFICE_SIGN_IN_URL } from '@/constants.ts';
import { setupAccountStateMessage } from '@/services/api/accountState';

interface Props {
  pageTitle: string;
  pageDescription: string;
  email: string;
  authToken: string;
  isValidLink: boolean;
  isPasswordSetup?: boolean;
  verifyTokenCallback: () => Promise<{ success: boolean; reason?: string; message?: string }>;
  setPasswordCallback: (password: string) => Promise<{ success: boolean; reason?: string; message?: string }>;
  userRoles: string[];
}

const UserPasswordPage = ({
  pageTitle,
  pageDescription,
  email,
  authToken,
  isValidLink,
  isPasswordSetup = false,
  verifyTokenCallback,
  setPasswordCallback,
  userRoles,
}: Props) => {
  const [initIsLoading, setInitIsLoading] = useState(isValidLink);
  const [verificationSuccessful, setVerificationSuccessful] = useState(false);

  const [isPasswordSubmitLoading, setIsPasswordSubmitLoading] = useState(false);
  const [passwordSubmitSuccessful, setPasswordSubmitSuccessful] = useState(true); // initially true so that we can set it to false if something goes wrong
  const [accountStateMessage, setAccountStateMessage] = useState(
    isPasswordSetup && !isValidLink ? (setupAccountStateMessage({ reason: 'LINK_INVALID' }, 'verify') ?? '') : '',
  );

  const navigate = useNavigate();
  const toast = useToast();

  const verifyAuthTokenWithEmail = useCallback(async () => {
    if (!authToken || !isValidLink) {
      if (isPasswordSetup) setAccountStateMessage(setupAccountStateMessage({ reason: 'LINK_INVALID' }, 'verify') ?? '');
      setInitIsLoading(false);
      setVerificationSuccessful(false);
    } else {
      try {
        const result = await verifyTokenCallback();
        const { success } = result;
        if (!success && isPasswordSetup) setAccountStateMessage(setupAccountStateMessage(result, 'verify') ?? '');
        setVerificationSuccessful(success);
      } catch (error) {
        setAccountStateMessage(
          setupAccountStateMessage(error, isPasswordSetup ? 'verify' : undefined) ??
            'This invitation could not be verified. Please try again.',
        );
        setVerificationSuccessful(false);
      } finally {
        setInitIsLoading(false);
      }
    }
  }, [authToken, email, isValidLink, isPasswordSetup]);

  useEffect(() => {
    verifyAuthTokenWithEmail();
  }, []);

  const showPasswordFailure = (error: unknown) => {
    setAccountStateMessage(
      setupAccountStateMessage(error, isPasswordSetup ? 'submit' : undefined) ??
        onboardingMessages.passwordSetupUnsuccessful,
    );
    const reason = error && typeof error === 'object' && 'reason' in error ? error.reason : undefined;
    if (
      isPasswordSetup &&
      (reason === 'LINK_EXPIRED' ||
        reason === 'LINK_INVALID' ||
        reason === 'ORGANIZATION_INACTIVE' ||
        reason === 'SETUP_IN_PROGRESS')
    ) {
      setVerificationSuccessful(false);
    }
    setPasswordSubmitSuccessful(false);
  };

  const onSubmitPasswordForm = async (password: string) => {
    setIsPasswordSubmitLoading(true);
    setAccountStateMessage('');
    setPasswordSubmitSuccessful(true);

    try {
      const result = await setPasswordCallback(password);
      const { success } = result;

      if (success) {
        toast({
          status: 'success',
          title: 'Success',
          description: 'Password change successful!',
          position: 'top-right',
        });

        if (userRoles.includes(roles.superAdmin) || userRoles.includes(roles.memberCare)) {
          window.location.replace(BACKOFFICE_SIGN_IN_URL);
        } else {
          navigate({ to: '/sign-in' });
        }
      } else {
        showPasswordFailure(result);
      }
    } catch (error) {
      showPasswordFailure(error);
    } finally {
      setIsPasswordSubmitLoading(false);
    }
  };

  if (initIsLoading) return <LoadingPage />;

  if (!verificationSuccessful) {
    return (
      <VStack paddingTop={'50px'}>
        <Image
          src={logoVariants.purple252x80}
          alt="my-omnia"
          _hover={{ cursor: 'pointer' }}
          width={'126px'}
          height={'40px'}
          onClick={() => {
            navigate({ to: authRoutes.signIn });
          }}
        />
        <VStack paddingTop={'30px'}>
          <Image
            alt="edit-icon"
            src={iconsPng.orangeExclamationMark}
            width={'120px'}
            height={'120px'}
            objectFit="cover"
            style={{ margin: '40px 0' }}
          />
          <Text textAlign={'center'} variant="loraTitle" color="text.mediumBlue" fontSize={'26px'}>
            {accountStateMessage || onboardingMessages.emailNotVerifiedSuccessfully}
          </Text>
          {!accountStateMessage && (
            <Text
              textAlign={'center'}
              color={'text.mediumBlue'}
              opacity={0.7}
              lineHeight={'20px'}
              fontWeight={'600'}
              paddingTop={'20px'}
            >
              {onboardingMessages.checkEmailOrContactAdmin}
            </Text>
          )}
        </VStack>
      </VStack>
    );
  }

  return (
    <BlueTopPage title={pageTitle} description={pageDescription} hideButtons centeredLogo>
      <VStack>
        {verificationSuccessful && (
          <NewPasswordForm onSubmit={onSubmitPasswordForm} isLoading={isPasswordSubmitLoading} />
        )}

        {!passwordSubmitSuccessful && (
          <Text variant={'error'}>{accountStateMessage || onboardingMessages.passwordSetupUnsuccessful}</Text>
        )}
      </VStack>
    </BlueTopPage>
  );
};

export default UserPasswordPage;
